import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, errMsg, fmtMoney } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, MapPin, CalendarDays, Truck, ShieldCheck,
  CreditCard, Copy, Smartphone, DollarSign, Lock, AlertCircle, MessageCircle, Send, Star, ExternalLink,
} from "lucide-react";
import { fmtTime } from "../../components/shared/ItemDetailsSheet";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";

const prettyDate = (iso) => {
  if (!iso) return "TBD";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString("en-US", {
      weekday: "short", month: "long", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
};

export default function JobLink() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [payKind, setPayKind] = useState("full");
  const [acceptForm, setAcceptForm] = useState({ password: "", name: "", phone: "", email: "" });
  const [track, setTrack] = useState(null);
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [reviewLink, setReviewLink] = useState("");

  const load = () => {
    setLoading(true);
    api.get(`/jobs/link/${token}`).then((r) => {
      setData(r.data);
      setAcceptForm((f) => ({ ...f,
        name: r.data.customer_name || "",
        phone: r.data.customer_phone || "",
        email: r.data.customer_email || "",
      }));
    }).catch((e) => setError(errMsg(e))).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);
  useEffect(() => { if (params.get("paid")) toast.success("Payment received — you're locked in!"); }, [params]);

  // Poll live tracking every 8 s while we have data
  useEffect(() => {
    if (!data) return;
    const fetchTrack = () => api.get(`/jobs/link/${token}/track`).then((r) => setTrack(r.data)).catch(() => {});
    fetchTrack();
    const id = setInterval(fetchTrack, 8000);
    return () => clearInterval(id);
  }, [data, token]);

  const loadChat = () => api.get(`/jobs/link/${token}/chat`).then((r) => setChat(r.data)).catch(() => {});
  useEffect(() => { if (chatOpen) { loadChat(); const id = setInterval(loadChat, 6000); return () => clearInterval(id); } /* eslint-disable-next-line */ }, [chatOpen]);

  // Fetch Google review link when the job hits completed
  useEffect(() => {
    if (data?.mission?.status === "completed" && !reviewLink) {
      api.get(`/jobs/link/${token}/review-link`).then((r) => setReviewLink(r.data.google_review_url || "")).catch(() => {});
    }
  }, [data?.mission?.status, token, reviewLink]);

  const sendChat = async () => {
    const t = chatInput.trim(); if (!t) return;
    setChatInput("");
    try { await api.post(`/jobs/link/${token}/chat`, { text: t }); loadChat(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (error) return (
    <div className="max-w-md mx-auto px-5 py-24 text-center">
      <AlertCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
      <h1 className="font-heading font-bold text-2xl">Link not valid</h1>
      <p className="text-muted-foreground mt-2">{error}</p>
      <Link to="/"><Button className="rounded-full mt-6">Back Home</Button></Link>
    </div>
  );

  const m = data.mission;
  const paid = m.paid_full;
  const needsAccept = !data.accepted && !user;

  const accept = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/jobs/link/${token}/accept`, acceptForm);
      setUser(r.user);
      toast.success("Account confirmed — welcome!");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const payWithStripe = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/jobs/link/${token}/checkout`, { origin_url: window.location.origin, kind: payKind });
      window.location.href = r.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  const declareManual = async (method) => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/jobs/link/${token}/manual-payment-intent`, { method, kind: payKind });
      toast.success(`Send ${fmtMoney(r.amount)} to ${r.handle || "our team"} — admin will confirm within 15 min.`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div className="min-h-screen bg-background pb-16" data-testid="job-link-page">
      {/* Hero header */}
      <div className="border-b bg-gradient-to-b from-[var(--cc-accent)]/8 to-transparent">
        <div className="max-w-3xl mx-auto px-5 py-8">
          <p className="overline-cc mb-1">CastleCorp Job Link</p>
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl">
              {m.mission_number}
              {paid && <span className="ml-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-500 text-xs font-bold px-3 py-1"><CheckCircle2 className="w-3.5 h-3.5" /> PAID</span>}
            </h1>
            <p className="font-heading font-extrabold text-3xl text-[var(--cc-accent)]" data-testid="job-link-total">{fmtMoney(m.value)}</p>
          </div>
          <p className="text-muted-foreground mt-2 text-base">Hi {data.customer_name.split(" ")[0]} — here's your mission briefing.</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-8 space-y-6">
        {/* Status timeline — horizontal cascade Booked → Assigned → En Route → In Progress → Completed */}
        <StatusTimeline status={m.status} />

        {/* LIVE map — activates once status is en_route / in_progress; static route pins otherwise */}
        <LiveMap track={track} m={m} />

        {/* Google review CTA — only after job is completed */}
        {m.status === "completed" && reviewLink && (
          <a href={reviewLink} target="_blank" rel="noreferrer" data-testid="job-link-google-review"
            className="block rounded-2xl border-2 border-[var(--cc-accent)]/40 bg-gradient-to-br from-[var(--cc-accent)]/10 to-transparent p-6 hover:from-[var(--cc-accent)]/15 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[var(--cc-accent)]/15 flex items-center justify-center shrink-0">
                <Star className="w-7 h-7 text-[var(--cc-accent)] fill-[var(--cc-accent)]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-extrabold text-xl">Loved the service?</p>
                <p className="text-sm text-muted-foreground">Leave us a 5★ review on Google — it takes 20 seconds and helps us grow.</p>
              </div>
              <ExternalLink className="w-5 h-5 text-[var(--cc-accent)] shrink-0" />
            </div>
          </a>
        )}

        {/* Mission details */}
        <div className="rounded-2xl border bg-card p-6 corner-cc space-y-4" data-testid="job-link-details">
          <p className="stat-label">Mission Details</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Row icon={Truck} label="Service" value={m.service_type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())} />
            <Row icon={CalendarDays} label="Scheduled" value={`${prettyDate(m.scheduled_date)}${m.scheduled_time ? ` · ${fmtTime(m.scheduled_time)}` : ""}`} />
            <Row icon={MapPin} label="Pickup" value={m.pickup_address} />
            {!m.same_place && m.dropoff_address && <Row icon={MapPin} label="Dropoff" value={m.dropoff_address} />}
          </div>

          {m.items?.length > 0 && (
            <div className="pt-3 border-t">
              <p className="text-xs font-mono-cc uppercase text-muted-foreground mb-2">Items</p>
              <div className="flex flex-wrap gap-2">
                {m.items.map((it, i) => (
                  <span key={i} className="text-sm px-3 py-1.5 rounded-full border bg-secondary/50 font-medium">
                    {it.name}{it.qty > 1 ? ` ×${it.qty}` : ""}{it.spec ? ` (${it.spec})` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}

          {m.pricing_breakdown && (
            <div className="pt-3 border-t space-y-1.5 text-sm" data-testid="job-link-breakdown">
              <p className="text-xs font-mono-cc uppercase text-muted-foreground mb-1">Price breakdown</p>
              <Line label={`Weight tier — ${m.pricing_breakdown.weight_tier_label} · ${m.pricing_breakdown.estimated_weight_lbs} lbs est.`} amt={m.pricing_breakdown.weight_tier_price} />
              {m.pricing_breakdown.distance_fee > 0 && <Line label={`Distance: ${m.pricing_breakdown.billable_miles} mi over ${m.pricing_breakdown.included_miles} mi`} amt={m.pricing_breakdown.distance_fee} />}
              {m.pricing_breakdown.stairs_fee > 0 && <Line label={`Stairs — ${m.pricing_breakdown.stairs_flights} flight(s)`} amt={m.pricing_breakdown.stairs_fee} />}
              {m.pricing_breakdown.electrical_fee > 0 && <Line label="Electrical disconnect" amt={m.pricing_breakdown.electrical_fee} />}
              {m.pricing_breakdown.stops_fee > 0 && <Line label={`Extra stops × ${m.pricing_breakdown.extra_stops}`} amt={m.pricing_breakdown.stops_fee} />}
              {m.pricing_breakdown.priority_fee > 0 && <Line label="Priority" amt={m.pricing_breakdown.priority_fee} />}
              {m.pricing_breakdown.special_delivery_handling > 0 && <Line label="Special-delivery handling" amt={m.pricing_breakdown.special_delivery_handling} />}
              <div className="border-t mt-2 pt-2 flex justify-between text-base font-heading font-bold">
                <span>Total</span><span>{fmtMoney(m.value)}</span>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
            <ShieldCheck className="w-4 h-4 text-[var(--cc-accent)]" />
            Crew, truck, equipment, protection & $1M insurance included.
          </div>
        </div>

        {/* Acceptance / Auth */}
        {needsAccept && (
          <div className="rounded-2xl border-2 border-[var(--cc-accent)]/40 bg-[var(--cc-accent)]/5 p-6" data-testid="job-link-accept">
            <p className="stat-label">
              {data.has_account ? "Sign In to Accept" : data.needs_customer_info ? "Claim Your Job" : "Accept & Lock In"}
            </p>
            <h2 className="font-heading font-bold text-2xl mt-2">
              {data.has_account
                ? `Welcome back, sign in to accept`
                : "Create your account in 10 seconds"}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {data.has_account
                ? `We recognize ${data.customer_email} — enter your existing CastleCorp password below.`
                : "One password — track your job, chat with the crew, and pay securely."}
            </p>
            <div className="space-y-3 mt-4">
              <div className="space-y-1"><Label className="text-xs font-bold">Email {data.needs_customer_info && "*"}</Label>
                {data.needs_customer_info ? (
                  <Input data-testid="job-link-email-input" type="email" placeholder="you@email.com" value={acceptForm.email}
                    onChange={(e) => setAcceptForm({ ...acceptForm, email: e.target.value })} className="h-11" />
                ) : (
                  <Input value={data.customer_email} disabled className="h-11 opacity-70" />
                )}
              </div>

              {!data.has_account && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs font-bold">Your name{data.needs_customer_info ? " *" : ""}</Label>
                    <Input data-testid="job-link-name-input" value={acceptForm.name} onChange={(e) => setAcceptForm({ ...acceptForm, name: e.target.value })} className="h-11" /></div>
                  <div className="space-y-1"><Label className="text-xs font-bold">Phone</Label>
                    <Input data-testid="job-link-phone-input" value={acceptForm.phone} onChange={(e) => setAcceptForm({ ...acceptForm, phone: e.target.value })} className="h-11" /></div>
                </div>
              )}

              <div className="space-y-1"><Label className="text-xs font-bold">{data.has_account ? "Your CastleCorp password" : "Create a password (6+ characters)"}</Label>
                <Input data-testid="job-link-password-input" type="password" value={acceptForm.password}
                  onChange={(e) => setAcceptForm({ ...acceptForm, password: e.target.value })} className="h-11" /></div>

              <Button data-testid="job-link-accept-button" onClick={accept}
                disabled={busy || acceptForm.password.length < 6 || (data.needs_customer_info && (!acceptForm.email || !acceptForm.name?.trim()))}
                className="w-full h-13 rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-bold text-base">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> :
                 data.has_account ? <><Lock className="w-4 h-4 mr-2" />Sign in & Accept</> :
                                   <><Lock className="w-4 h-4 mr-2" />Accept Job & Create Account</>}
              </Button>

              {!data.has_account && !data.needs_customer_info && (
                <p className="text-xs text-muted-foreground text-center">Already have a CastleCorp account? Sign in from the <Link to="/login" className="underline">login page</Link> first.</p>
              )}
              {data.has_account && (
                <p className="text-xs text-muted-foreground text-center">Forgot your password? <Link to="/login" className="underline">Reset here</Link>.</p>
              )}
            </div>
          </div>
        )}

        {/* Payment */}
        {!paid && (data.accepted || user) && (
          <div className="rounded-2xl border bg-card p-6 space-y-4" data-testid="job-link-payment">
            <div className="flex items-baseline justify-between">
              <p className="stat-label">Pay Securely</p>
              <div className="flex text-xs font-semibold rounded-full bg-secondary p-1">
                {[["full", `Full ${fmtMoney(m.value)}`], ["deposit", `Deposit ${fmtMoney(m.deposit_amount)}`]].map(([k, label]) => (
                  <button key={k} data-testid={`job-link-pay-kind-${k}`} onClick={() => setPayKind(k)}
                    className={`px-3 py-1.5 rounded-full transition-colors ${payKind === k ? "bg-[var(--cc-accent)] text-black" : "text-muted-foreground"}`}>{label}</button>
                ))}
              </div>
            </div>
            <div className="grid gap-3">
              <Button data-testid="job-link-pay-stripe" onClick={payWithStripe} disabled={busy}
                className="h-14 rounded-2xl bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-bold btn-glow text-base justify-start px-6">
                <CreditCard className="w-5 h-5 mr-3" /> Card / Apple Pay / Google Pay
                <span className="ml-auto font-mono-cc">Instant</span>
              </Button>
              <Button variant="outline" data-testid="job-link-pay-venmo" onClick={() => declareManual("venmo")} disabled={busy}
                className="h-14 rounded-2xl font-semibold text-base justify-start px-6 hover:border-[var(--cc-accent)]/60">
                <Smartphone className="w-5 h-5 mr-3 text-[#3D95CE]" /> Venmo
                <span className="ml-auto font-mono-cc text-xs opacity-70">Manual verify</span>
              </Button>

              {process.env.REACT_APP_PAYPAL_CLIENT_ID && (
                <div className="rounded-2xl border p-4 bg-secondary/30" data-testid="job-link-paypal-wrapper">
                  <p className="text-xs font-mono-cc uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.34 2.596 1.007 4.31-.005.021-.01.05-.014.076 1.617.85 2.437 2.377 2.437 4.564 0 3.916-3.207 6.517-7.972 6.517H12.06a.641.641 0 0 0-.633.542l-.978 6.198-.076.412a.32.32 0 0 1-.315.267H7.076z"/></svg>
                    Or checkout with PayPal / Debit / Credit Card
                  </p>
                  <PayPalScriptProvider
                    options={{
                      "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
                      currency: "USD",
                      intent: "capture",
                      "enable-funding": "venmo,paylater,card",
                    }}
                  >
                    <PayPalButtons
                      key={`${token}-${payKind}-${m.value}`}
                      style={{ layout: "vertical", label: "paypal", shape: "pill", height: 48 }}
                      disabled={busy}
                      createOrder={async () => {
                        const { data: r } = await api.post("/paypal/orders", { job_token: token, kind: payKind });
                        return r.id;
                      }}
                      onApprove={async (d) => {
                        const { data: r } = await api.post(`/paypal/orders/${d.orderID}/capture`, { order_id: d.orderID });
                        if (r.status === "COMPLETED") {
                          toast.success("PayPal payment received — you're locked in!");
                          load();
                        } else {
                          toast.error(`Payment not completed: ${r.status}`);
                        }
                      }}
                      onError={(err) => { console.error(err); toast.error("PayPal checkout failed. Please try another option."); }}
                      onCancel={() => toast.info("PayPal checkout cancelled")}
                    />
                  </PayPalScriptProvider>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed border-t pt-3">
              Stripe releases funds after the job is completed and you sign off — your money stays protected until then.
              Venmo &amp; PayPal are marked pending until an admin confirms receipt.
            </p>
          </div>
        )}

        {/* Timeline */}
        {m.timeline?.length > 0 && (
          <div className="rounded-2xl border bg-card p-6" data-testid="job-link-timeline">
            <p className="stat-label mb-4">Job Timeline</p>
            <div className="space-y-3">
              {m.timeline.slice().reverse().slice(0, 6).map((t, i) => (
                <div key={i} className="flex gap-3 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-[var(--cc-accent)] mt-2 shrink-0" />
                  <div className="flex-1"><p>{t.label}</p><p className="text-xs text-muted-foreground mt-0.5">{new Date(t.ts).toLocaleString()}</p></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {user && (
          <div className="flex justify-center pt-4">
            <Button variant="outline" onClick={() => nav("/portal/jobs")} className="rounded-full" data-testid="job-link-portal-button">
              Open my portal →
            </Button>
          </div>
        )}
      </div>

      {/* Chat FAB */}
      <button data-testid="job-link-chat-fab" onClick={() => setChatOpen((v) => !v)}
        className="fixed bottom-5 right-5 z-40 rounded-full pl-4 pr-5 h-14 bg-[var(--cc-accent)] text-black font-bold btn-glow shadow-2xl flex items-center gap-2 hover:scale-105 transition-transform">
        <MessageCircle className="w-5 h-5" />
        <span className="hidden sm:inline text-sm">Chat</span>
      </button>

      {/* Chat panel */}
      {chatOpen && (
        <div className="fixed inset-x-0 bottom-0 sm:right-5 sm:bottom-24 sm:inset-x-auto sm:w-96 z-50 rounded-t-3xl sm:rounded-3xl border bg-background shadow-2xl flex flex-col overflow-hidden"
          style={{ maxHeight: "70vh" }} data-testid="job-link-chat-panel">
          <div className="flex items-center justify-between px-4 h-12 border-b bg-card">
            <p className="font-heading font-bold text-sm">Job Chat · {m.mission_number}</p>
            <button onClick={() => setChatOpen(false)} className="w-8 h-8 rounded-full hover:bg-secondary flex items-center justify-center" aria-label="Close">
              <span className="text-lg leading-none">×</span>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5">
            {chat.length === 0 && <p className="text-center text-xs text-muted-foreground py-6">No messages yet. Ask us anything about your job.</p>}
            {chat.map((c) => (
              <div key={c.id} className={`flex ${c.sender === "customer" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${c.sender === "customer" ? "bg-[var(--cc-accent)] text-black" : "bg-secondary"}`}>
                  <p>{c.text}</p>
                  <p className="text-[10px] opacity-70 mt-1">{c.sender_name}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t flex gap-2 bg-card">
            <Input value={chatInput} onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendChat()}
              placeholder="Type a message..." data-testid="job-link-chat-input" className="rounded-full h-11" />
            <Button size="icon" onClick={sendChat} data-testid="job-link-chat-send"
              className="w-11 h-11 rounded-full bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 shrink-0"><Send className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5">
    <Icon className="w-4 h-4 text-[var(--cc-accent)] mt-1 shrink-0" />
    <div className="min-w-0"><p className="text-xs text-muted-foreground">{label}</p><p className="text-sm font-semibold truncate">{value || "—"}</p></div>
  </div>
);

const Line = ({ label, amt }) => (
  <div className="flex justify-between items-center">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-mono-cc font-semibold">{fmtMoney(amt)}</span>
  </div>
);

const TIMELINE_STAGES = [
  { key: "booked",      label: "Booked",       matches: ["booked", "pending_customer"] },
  { key: "assigned",    label: "Assigned",     matches: ["confirmed", "assigned"] },
  { key: "en_route",    label: "Crew En Route", matches: ["en_route", "enroute"] },
  { key: "in_progress", label: "In Progress",  matches: ["in_progress"] },
  { key: "completed",   label: "Completed",    matches: ["completed", "paid"] },
];

function StatusTimeline({ status }) {
  const activeIdx = Math.max(0, TIMELINE_STAGES.findIndex((s) => s.matches.includes(status)));
  return (
    <div className="rounded-2xl border bg-card p-5" data-testid="job-link-status-timeline">
      <p className="stat-label mb-3">Mission Status</p>
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {TIMELINE_STAGES.map((s, i) => {
          const done = i < activeIdx, active = i === activeIdx;
          return (
            <div key={s.key} className="flex items-center gap-2 shrink-0">
              <div className={`flex items-center gap-2 rounded-full pl-2.5 pr-3.5 py-1.5 text-xs font-semibold border transition-colors ${
                active ? "border-[var(--cc-accent)] bg-[var(--cc-accent)]/15 text-[var(--cc-accent)]" :
                done ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-500" :
                       "border-border text-muted-foreground"}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
                  active ? "bg-[var(--cc-accent)] text-black" :
                  done ? "bg-emerald-500 text-black" : "bg-secondary"}`}>
                  {done ? "✓" : i + 1}
                </span>
                {s.label}
              </div>
              {i < TIMELINE_STAGES.length - 1 && (
                <div className={`w-6 h-px ${i < activeIdx ? "bg-emerald-500" : "bg-border"}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const dotIcon = (color) => L.divIcon({
  className: "custom-dot",
  html: `<span style="display:block;width:16px;height:16px;border-radius:50%;background:${color};box-shadow:0 0 0 3px rgba(255,255,255,.5),0 2px 6px rgba(0,0,0,.35)"></span>`,
  iconSize: [16, 16], iconAnchor: [8, 8],
});

function LiveMap({ track, m }) {
  const positions = track?.crew_positions || [];
  const first = positions[0];
  const pickup = track?.pickup || m?.pickup_coords;
  const dropoff = track?.dropoff || m?.dropoff_coords;
  const center = first ? [first.lat, first.lng] : pickup ? [pickup.lat, pickup.lng] : [32.7157, -117.1611];
  const live = track?.live;
  return (
    <div className="rounded-2xl border overflow-hidden bg-card" data-testid="job-link-live-map">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div>
          <p className="stat-label">Live Map</p>
          <p className="text-xs text-muted-foreground">
            {live ? `Tracking ${positions.length || "your"} crew now` : "Route preview — live tracking activates when the crew is en route"}
          </p>
        </div>
        {live && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-500 text-[11px] font-bold px-2.5 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
          </span>
        )}
      </div>
      <div style={{ height: 260 }}>
        <MapContainer center={center} zoom={11} scrollWheelZoom={false} className="h-full w-full map-dark-tiles">
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
          {pickup?.lat && (
            <Marker position={[pickup.lat, pickup.lng]} icon={dotIcon("#22c55e")}>
              <Popup>Pickup</Popup>
            </Marker>
          )}
          {dropoff?.lat && (
            <Marker position={[dropoff.lat, dropoff.lng]} icon={dotIcon("#ef4444")}>
              <Popup>Dropoff</Popup>
            </Marker>
          )}
          {positions.map((p, i) => (
            <Marker key={i} position={[p.lat, p.lng]} icon={dotIcon("#fbbf24")}>
              <Popup>{p.name} · Live crew</Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}
